'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbProps {
  className?: string;
  customItems?: BreadcrumbItem[];
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  isActive?: boolean;
}

const Breadcrumb = ({ className = '', customItems }: BreadcrumbProps) => {
  const pathname = usePathname();

  const routeLabels: Record<string, string> = {
    '/home': 'News',
    '/article-detail': 'Article Detail',
    '/marketplace-listings': 'Equipment Marketplace',
    '/equipment-detail': 'Equipment Detail',
    '/jobs-board': 'Jobs Board',
    '/job-detail': 'Job Detail',
    '/user-dashboard': 'Dashboard',
    '/authentication': 'Authentication',
    '/admin-dashboard': 'Admin Dashboard',
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const pathSegments = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/home' }
    ];

    if (pathname === '/home') {
      return [{ label: 'News', isActive: true }];
    }

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      breadcrumbs.push({
        label: routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1),
        path: isLast ? undefined : currentPath,
        isActive: isLast
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav className={`flex items-center space-x-2 text-sm ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <Icon 
                name="ChevronRightIcon" 
                size={16} 
                className="text-text-secondary mx-2" 
              />
            )}
            
            {item.isActive ? (
              <span className="font-heading font-bold text-foreground uppercase tracking-wide">
                {item.label}
              </span>
            ) : (
              <Link
                href={item.path || '#'}
                className="font-heading text-text-secondary hover:text-primary transition-industrial hover-underline uppercase tracking-wide"
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;